import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  useDataProvider,
  useNotify,
  useGetIdentity,
  type Identifier,
} from "ra-core";
import { format, parseISO, isValid } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  User,
  Building2,
  Handshake,
  Video,
  X,
  Calendar,
  Trash2,
  Copy,
  ExternalLink,
  Sparkles,
  FileText,
  Mail,
  Users,
  Plus,
  Eye,
  Edit2,
} from "lucide-react";
import type { Task, Deal, Contact, Company, Sale } from "../types";
import { appendDealHistoryDirect } from "../deals/useDealHistory";
import { ActivityNoteBox } from "../misc/ActivityNoteBox";
import { NoteFormattingToolbar } from "../misc/NoteFormattingToolbar";
import {
  getConnectedGoogleUser,
  syncTaskWithGoogleCalendar,
  deleteEventFromGoogleCalendar,
  createGoogleMeetRoom,
  connectGoogleOAuth,
  getGCalEventId,
  setGCalEventId,
  getGCalHtmlLink,
  getTaskAttendees,
  setTaskAttendees,
  isTaskTypeAllowedForGCalSync,
} from "../providers/google/googleIntegrationService";
import { ptBR } from "date-fns/locale";
import { ACTIVITY_TYPES } from "./activityTypes";
import { ActivityMiniDayTimeline } from "./ActivityMiniDayTimeline";
import { ActivityCallButton } from "./ActivityCallButton";
import { cn } from "@/lib/utils";

interface ActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskToEdit?: Task | null;
  defaultDealId?: Identifier | null;
  defaultCompanyId?: Identifier | null;
  defaultContactId?: Identifier | null;
  defaultDate?: Date;
  /** Vendedor pré-selecionado como proprietário da atividade */
  defaultSalesId?: Identifier | null;
  onSuccess?: () => void;
}

export const ActivityDialog = ({
  open,
  onOpenChange,
  taskToEdit,
  defaultDealId,
  defaultCompanyId,
  defaultContactId,
  defaultDate,
  defaultSalesId,
  onSuccess,
}: ActivityDialogProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();

  const [text, setText] = useState("");
  const userModifiedTitleRef = useRef(false);
  const [notes, setNotes] = useState("");
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [previewNotes, setPreviewNotes] = useState(false);
  const [type, setType] = useState("chamada");

  // Helper para verificar se o título atual foi gerado automaticamente
  const isAutoGeneratedTitle = useCallback((titleStr: string): boolean => {
    const trimmed = titleStr.trim();
    if (!trimmed) return true;
    const typeLabels = Object.values(ACTIVITY_TYPES).map((t) => t.label);
    return typeLabels.some(
      (label) => trimmed === label || trimmed.startsWith(`${label} - `),
    );
  }, []);
  const [prioridade, setPrioridade] = useState<"baixa" | "media" | "alta">(
    "media",
  );
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [linkChamada, setLinkChamada] = useState("");
  const [convidados, setConvidados] = useState<string[]>([]);
  const [novoConvidado, setNovoConvidado] = useState("");
  const [dealId, setDealId] = useState<string>("none");
  const [contactId, setContactId] = useState<string>("none");
  const [companyId, setCompanyId] = useState<string>("none");
  const [isDone, setIsDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingMeet, setGeneratingMeet] = useState(false);
  const [syncGoogleCalendar, setSyncGoogleCalendar] = useState(true);
  const [generatedGcalEventId, setGeneratedGcalEventId] = useState<
    string | null
  >(null);
  const [generatedGcalSalesId, setGeneratedGcalSalesId] = useState<
    string | number | null
  >(null);

  // Available options
  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [salesId, setSalesId] = useState<string>("none");

  // Fetch associations
  useEffect(() => {
    if (!open) return;
    dataProvider
      .getList("deals", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then((res) => setDeals(res.data as Deal[]))
      .catch(() => {});

    dataProvider
      .getList("contacts", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "first_name", order: "ASC" },
        filter: {},
      })
      .then((res) => setContacts(res.data as Contact[]))
      .catch(() => {});

    dataProvider
      .getList("companies", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then((res) => setCompanies(res.data as Company[]))
      .catch(() => {});

    dataProvider
      .getList("sales", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "first_name", order: "ASC" },
        filter: {},
      })
      .then((res) => setSales(res.data as Sale[]))
      .catch(() => {});
  }, [open, dataProvider]);

  // Handler para troca de tipo com atualização automática de título
  const handleTypeSelect = useCallback(
    (newType: string) => {
      setType(newType);
      const newTypeLabel = ACTIVITY_TYPES[newType]?.label || "Chamada";
      if (
        !userModifiedTitleRef.current ||
        !text.trim() ||
        isAutoGeneratedTitle(text)
      ) {
        const currentDeal = deals.find((d) => String(d.id) === String(dealId));
        if (currentDeal && currentDeal.name) {
          setText(`${newTypeLabel} - ${currentDeal.name}`);
        } else if (!text.trim() || isAutoGeneratedTitle(text)) {
          setText(newTypeLabel);
        }
      }
    },
    [dealId, deals, isAutoGeneratedTitle, text],
  );

  // Função para vincular automaticamente pessoa e empresa ao escolher um negócio
  const handleDealChange = useCallback(
    async (newDealId: string) => {
      setDealId(newDealId);
      const typeLabel = ACTIVITY_TYPES[type]?.label || "Chamada";

      if (!newDealId || newDealId === "none") {
        if (!userModifiedTitleRef.current || isAutoGeneratedTitle(text)) {
          setText(typeLabel);
        }
        return;
      }

      let currentDeal = deals.find((d) => String(d.id) === String(newDealId));

      if (currentDeal && currentDeal.name) {
        if (
          !userModifiedTitleRef.current ||
          !text.trim() ||
          isAutoGeneratedTitle(text)
        ) {
          setText(`${typeLabel} - ${currentDeal.name}`);
        }
      }

      try {
        if (!currentDeal) {
          const res = await dataProvider.getOne<Deal>("deals", {
            id: newDealId,
          });
          currentDeal = res.data;
          if (currentDeal) {
            setDeals((prev) =>
              prev.some((d) => String(d.id) === String(currentDeal!.id))
                ? prev
                : [...prev, currentDeal!],
            );
            if (
              !userModifiedTitleRef.current ||
              !text.trim() ||
              isAutoGeneratedTitle(text)
            ) {
              setText(`${typeLabel} - ${currentDeal.name}`);
            }
          }
        }

        if (currentDeal) {
          if (currentDeal.contact_ids?.[0]) {
            const cId = String(currentDeal.contact_ids[0]);
            setContactId(cId);
            if (!contacts.some((c) => String(c.id) === cId)) {
              dataProvider
                .getOne<Contact>("contacts", { id: cId })
                .then((res) => {
                  if (res.data) {
                    setContacts((prev) => [...prev, res.data]);
                  }
                })
                .catch(() => {});
            }
          }

          if (currentDeal.company_id) {
            const compId = String(currentDeal.company_id);
            setCompanyId(compId);
            if (!companies.some((c) => String(c.id) === compId)) {
              dataProvider
                .getOne<Company>("companies", { id: compId })
                .then((res) => {
                  if (res.data) {
                    setCompanies((prev) => [...prev, res.data]);
                  }
                })
                .catch(() => {});
            }
          }
        }
      } catch (err) {
        console.error("Erro ao sincronizar dados do negócio:", err);
      }
    },
    [
      deals,
      contacts,
      companies,
      dataProvider,
      isAutoGeneratedTitle,
      text,
      type,
    ],
  );

  // Populate form on open or taskToEdit change
  useEffect(() => {
    if (!open) return;

    if (taskToEdit) {
      userModifiedTitleRef.current = true;
      setText(taskToEdit.text || "");
      setNotes(taskToEdit.notes || "");
      setType(taskToEdit.type || "chamada");
      setPrioridade(taskToEdit.prioridade || "media");
      setDataInicio(
        taskToEdit.data_inicio
          ? format(new Date(taskToEdit.data_inicio), "yyyy-MM-dd'T'HH:mm")
          : taskToEdit.due_date
            ? format(new Date(taskToEdit.due_date), "yyyy-MM-dd'T'HH:mm")
            : "",
      );
      setDataFim(
        taskToEdit.data_fim
          ? format(new Date(taskToEdit.data_fim), "yyyy-MM-dd'T'HH:mm")
          : "",
      );
      setLinkChamada(taskToEdit.link_chamada || "");
      const storedGuests = taskToEdit.id ? getTaskAttendees(taskToEdit.id) : [];
      const initialGuests =
        taskToEdit.convidados && taskToEdit.convidados.length > 0
          ? taskToEdit.convidados
          : storedGuests;
      setConvidados(initialGuests || []);
      setNovoConvidado("");
      setDealId(taskToEdit.deal_id ? String(taskToEdit.deal_id) : "none");
      setContactId(
        taskToEdit.contact_id ? String(taskToEdit.contact_id) : "none",
      );
      setCompanyId(
        taskToEdit.company_id ? String(taskToEdit.company_id) : "none",
      );
      setIsDone(Boolean(taskToEdit.done_date));
      setSalesId(taskToEdit.sales_id ? String(taskToEdit.sales_id) : "none");

      // Garante que o negócio vinculado à tarefa em edição esteja na lista
      if (taskToEdit.deal_id) {
        dataProvider
          .getOne<Deal>("deals", { id: taskToEdit.deal_id })
          .then(({ data }) => {
            if (data) {
              setDeals((prev) =>
                prev.some((d) => String(d.id) === String(data.id))
                  ? prev
                  : [data, ...prev],
              );
            }
          })
          .catch(() => {});
      }

      // Garante que o contato vinculado à tarefa em edição esteja na lista
      if (taskToEdit.contact_id) {
        dataProvider
          .getOne<Contact>("contacts", { id: taskToEdit.contact_id })
          .then(({ data }) => {
            if (data) {
              setContacts((prev) =>
                prev.some((c) => String(c.id) === String(data.id))
                  ? prev
                  : [data, ...prev],
              );
            }
          })
          .catch(() => {});
      }

      // Garante que a empresa vinculada à tarefa em edição esteja na lista
      if (taskToEdit.company_id) {
        dataProvider
          .getOne<Company>("companies", { id: taskToEdit.company_id })
          .then(({ data }) => {
            if (data) {
              setCompanies((prev) =>
                prev.some((c) => String(c.id) === String(data.id))
                  ? prev
                  : [data, ...prev],
              );
            }
          })
          .catch(() => {});
      }
    } else {
      userModifiedTitleRef.current = false;
      const now = defaultDate || new Date();
      setNotes("");
      setType("chamada");
      setPrioridade("media");
      setDataInicio(format(now, "yyyy-MM-dd'T'HH:mm"));
      setDataFim("");
      setLinkChamada("");
      setConvidados([]);
      setNovoConvidado("");
      setIsDone(false);
      setGeneratedGcalEventId(null);
      setGeneratedGcalSalesId(null);

      if (defaultDealId) {
        const found = deals.find((d) => String(d.id) === String(defaultDealId));
        if (found && found.name) {
          setText(`Chamada - ${found.name}`);
        } else {
          setText("Chamada");
        }
      } else {
        setText("Chamada");
      }

      const initialSalesId = defaultSalesId
        ? String(defaultSalesId)
        : identity?.id
          ? String(identity.id)
          : "none";
      setSalesId(initialSalesId);

      const initialDealId = defaultDealId ? String(defaultDealId) : "none";
      const initialContactId = defaultContactId
        ? String(defaultContactId)
        : "none";
      const initialCompanyId = defaultCompanyId
        ? String(defaultCompanyId)
        : "none";

      setDealId(initialDealId);
      setContactId(initialContactId);
      setCompanyId(initialCompanyId);

      if (defaultDealId) {
        dataProvider
          .getOne<Deal>("deals", { id: defaultDealId })
          .then(({ data }) => {
            if (data) {
              setDeals((prev) =>
                prev.some((d) => String(d.id) === String(data.id))
                  ? prev
                  : [...prev, data],
              );
              if (
                !taskToEdit &&
                (!userModifiedTitleRef.current || isAutoGeneratedTitle(text))
              ) {
                setText(`Chamada - ${data.name}`);
              }
              if (!defaultContactId && data.contact_ids?.[0]) {
                const autoContactId = String(data.contact_ids[0]);
                setContactId(autoContactId);
                dataProvider
                  .getOne<Contact>("contacts", { id: autoContactId })
                  .then((cRes) => {
                    if (cRes.data) {
                      setContacts((prev) =>
                        prev.some((c) => String(c.id) === String(cRes.data.id))
                          ? prev
                          : [...prev, cRes.data],
                      );
                    }
                  })
                  .catch(() => {});
              }
              if (!defaultCompanyId && data.company_id) {
                const autoCompanyId = String(data.company_id);
                setCompanyId(autoCompanyId);
                dataProvider
                  .getOne<Company>("companies", { id: autoCompanyId })
                  .then((compRes) => {
                    if (compRes.data) {
                      setCompanies((prev) =>
                        prev.some(
                          (c) => String(c.id) === String(compRes.data.id),
                        )
                          ? prev
                          : [...prev, compRes.data],
                      );
                    }
                  })
                  .catch(() => {});
              }
            }
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taskToEdit,
    defaultDate,
    defaultDealId,
    defaultCompanyId,
    defaultContactId,
    defaultSalesId,
    identity,
    open,
    dataProvider,
  ]);

  // Data selecionada para o mini-calendário
  const selectedCalendarDate = useMemo(() => {
    if (dataInicio) {
      try {
        const d = parseISO(dataInicio);
        if (isValid(d)) return d;
      } catch {
        // fallback
      }
    }
    return defaultDate || new Date();
  }, [dataInicio, defaultDate]);

  // Horas decompostas
  const startDateStr = dataInicio
    ? dataInicio.split("T")[0]
    : format(new Date(), "yyyy-MM-dd");
  const startTimeStr = dataInicio
    ? dataInicio.split("T")[1]?.substring(0, 5) || "10:00"
    : "10:00";
  const endDateStr = dataFim ? dataFim.split("T")[0] : "";
  const endTimeStr = dataFim
    ? dataFim.split("T")[1]?.substring(0, 5) || ""
    : "";

  // Quando o usuário navega o dia no mini calendário
  const handleTimelineDateChange = (newDate: Date) => {
    const formatted = format(newDate, "yyyy-MM-dd");
    setDataInicio(`${formatted}T${startTimeStr}`);
    if (dataFim && endTimeStr) {
      setDataFim(`${formatted}T${endTimeStr}`);
    }
  };

  // Quando o usuário clica num horário livre na timeline
  const handleTimelineSlotSelect = (startHour: number, startMinute: number) => {
    const sHour = String(startHour).padStart(2, "0");
    const sMin = String(startMinute).padStart(2, "0");

    setDataInicio(`${startDateStr}T${sHour}:${sMin}`);
    if (dataFim && endTimeStr) {
      const eHour = String(Math.min(23, startHour + 1)).padStart(2, "0");
      setDataFim(`${startDateStr}T${eHour}:${sMin}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) {
      notify("Digite o título ou descrição da atividade", { type: "warning" });
      return;
    }

    try {
      setSaving(true);
      const isoInicio = dataInicio
        ? new Date(dataInicio).toISOString()
        : new Date().toISOString();
      const isoFim = dataFim ? new Date(dataFim).toISOString() : null;

      const payload: any = {
        text: text.trim(),
        type,
        prioridade,
        data_inicio: isoInicio,
        due_date: isoInicio,
        data_fim: isoFim,
        link_chamada: linkChamada.trim() || null,
        convidados: convidados.length > 0 ? convidados : null,
        notes: notes.trim() || null,
        deal_id: dealId !== "none" ? Number(dealId) : null,
        contact_id: contactId !== "none" ? Number(contactId) : null,
        company_id: companyId !== "none" ? Number(companyId) : null,
        sales_id:
          salesId !== "none"
            ? Number(salesId)
            : identity?.id
              ? Number(identity.id)
              : null,
        done_date: isDone
          ? taskToEdit?.done_date || new Date().toISOString()
          : null,
        created_at: taskToEdit?.created_at || new Date().toISOString(),
      };

      let savedTaskId: any = undefined;

      if (taskToEdit) {
        try {
          await dataProvider.update("tasks", {
            id: taskToEdit.id,
            data: payload,
            previousData: taskToEdit,
          });
        } catch {
          // Se a coluna 'notes', 'convidados' ou 'created_at' ainda não tiver sido criada no Supabase remoto, salva os demais campos
          const {
            notes: _,
            convidados: __,
            created_at: ___,
            ...fallbackPayload
          } = payload;
          await dataProvider.update("tasks", {
            id: taskToEdit.id,
            data: fallbackPayload,
            previousData: taskToEdit,
          });
        }

        if (payload.deal_id && isDone && !taskToEdit.done_date) {
          appendDealHistoryDirect(payload.deal_id, {
            type: "activity_completed",
            label: "Atividade concluída",
            description: `atividade concluída: "${payload.text}" (${type || "Tarefa"})`,
            new_value: payload.text,
          });
        }
        notify("Atividade atualizada com sucesso", { type: "success" });
      } else {
        let created: any;
        try {
          created = await dataProvider.create("tasks", {
            data: payload,
          });
        } catch {
          const {
            notes: _,
            convidados: __,
            created_at: ___,
            ...fallbackPayload
          } = payload;
          created = await dataProvider.create("tasks", {
            data: fallbackPayload,
          });
        }
        savedTaskId = created?.data?.id;
        if (payload.deal_id) {
          const dateLabel = format(
            new Date(isoInicio),
            "dd/MM/yyyy 'às' HH:mm",
            {
              locale: ptBR,
            },
          );
          appendDealHistoryDirect(payload.deal_id, {
            type: "activity_created",
            label: "Atividade agendada",
            description: `atividade criada: "${payload.text}" (${type || "Tarefa"}) para ${dateLabel}`,
            new_value: payload.text,
          });
        }
        notify("Atividade agendada com sucesso", { type: "success" });
      }

      // Se a sala do Meet foi gerada provisoriamente na conta de outro usuário (ex: criador para outro responsável):
      if (
        generatedGcalEventId &&
        generatedGcalSalesId &&
        payload.sales_id &&
        String(generatedGcalSalesId) !== String(payload.sales_id)
      ) {
        deleteEventFromGoogleCalendar({
          eventId: generatedGcalEventId,
          salesId: generatedGcalSalesId,
        }).catch(() => {});
      }

      // Persiste os convidados localmente associados ao ID da tarefa
      const targetId = taskToEdit ? taskToEdit.id : savedTaskId;
      if (targetId) {
        setTaskAttendees(targetId, convidados);
      }

      // Sincronização automática com o Google Agenda (Calendar):
      // 1. Prioridade: Proprietário/Responsável (payload.sales_id)
      // 2. Fallback: Criador da atividade (identity?.id)
      // 3. Se os dois estiverem desconectados: NÃO cria de nenhum
      const ownerSalesId =
        payload.sales_id && payload.sales_id !== "none"
          ? payload.sales_id
          : null;
      const creatorSalesId = identity?.id || null;

      let effectiveGcalSalesId: string | number | null = null;
      if (ownerSalesId && getConnectedGoogleUser(ownerSalesId)) {
        effectiveGcalSalesId = ownerSalesId;
      } else if (creatorSalesId && getConnectedGoogleUser(creatorSalesId)) {
        effectiveGcalSalesId = creatorSalesId;
      }

      if (syncGoogleCalendar && effectiveGcalSalesId) {
        const targetGcalEventId =
          taskToEdit?.google_calendar_event_id ||
          (generatedGcalSalesId &&
          String(generatedGcalSalesId) === String(effectiveGcalSalesId)
            ? generatedGcalEventId
            : null) ||
          (targetId ? getGCalEventId(targetId) : null);
        const isMeeting =
          payload.type === "reuniao" ||
          payload.text?.toLowerCase().includes("reunião");
        syncTaskWithGoogleCalendar({
          id: targetId,
          sales_id: effectiveGcalSalesId,
          eventId: targetGcalEventId || undefined,
          google_calendar_event_id: targetGcalEventId || undefined,
          text: payload.text,
          type: payload.type,
          notes: payload.notes,
          data_inicio: isoInicio,
          data_fim: isoFim,
          link_chamada: payload.link_chamada,
          createMeetLink: !payload.link_chamada && isMeeting,
          attendees: convidados.length > 0 ? convidados : undefined,
        })
          .then((res) => {
            if (res.success && res.eventId) {
              setGeneratedGcalEventId(null);
              setGeneratedGcalSalesId(null);
              const updates: any = {
                google_calendar_event_id: res.eventId,
                google_calendar_html_link: res.htmlLink || null,
              };
              if (res.meetLink && !payload.link_chamada) {
                updates.link_chamada = res.meetLink;
              }

              // Persiste o vínculo e o link do Meet diretamente no banco de dados do Supabase
              dataProvider
                .update("tasks", {
                  id: targetId,
                  data: {
                    ...payload,
                    ...updates,
                  },
                  previousData: taskToEdit || payload,
                })
                .catch(() => {});

              notify(
                res.meetLink
                  ? "Atividade sincronizada e sala do Google Meet criada!"
                  : res.updated
                    ? "Atividade atualizada no Google Agenda!"
                    : "Compromisso sincronizado com o Google Agenda!",
                { type: "info" },
              );
            } else if (!res.success && res.needsAuth) {
              notify(
                "A sessão Google expirou. Conecte o Google novamente ao editar a atividade.",
                { type: "info" },
              );
            }
          })
          .catch(() => {});
      } else {
        setGeneratedGcalEventId(null);
        setGeneratedGcalSalesId(null);
      }

      onOpenChange(false);
      onSuccess?.();
    } catch {
      notify("Erro ao salvar atividade", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToEdit) return;
    if (
      !window.confirm(
        "Deseja realmente excluir esta atividade? Ela também será removida do Google Agenda caso esteja conectada.",
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      const gcalId =
        taskToEdit.google_calendar_event_id || getGCalEventId(taskToEdit.id);
      if (gcalId) {
        await deleteEventFromGoogleCalendar({
          taskId: taskToEdit.id,
          eventId: gcalId,
          salesId: taskToEdit.sales_id,
        });
      }

      await dataProvider.delete("tasks", {
        id: taskToEdit.id,
        previousData: taskToEdit,
      });

      if (taskToEdit.deal_id) {
        appendDealHistoryDirect(taskToEdit.deal_id, {
          type: "activity_deleted",
          label: "Atividade excluída",
          description: `atividade excluída: "${taskToEdit.text}"`,
          old_value: taskToEdit.text,
        });
      }

      notify("Atividade excluída com sucesso do CRM e do Google Agenda!", {
        type: "success",
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      notify("Erro ao excluir atividade", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateMeet = async () => {
    // 1. Prioridade: Proprietário/Responsável (salesId)
    // 2. Se o proprietário não estiver conectado: Criador da atividade (identity?.id)
    // 3. Se os dois estiverem desconectados: NÃO cria de nenhum
    const ownerSalesId = salesId !== "none" ? salesId : null;
    const creatorSalesId = identity?.id || null;

    const ownerGoogleUser = ownerSalesId
      ? getConnectedGoogleUser(ownerSalesId)
      : null;
    const creatorGoogleUser = creatorSalesId
      ? getConnectedGoogleUser(creatorSalesId)
      : null;

    let targetSalesId: string | number | null = null;
    let targetName = "";

    if (ownerGoogleUser) {
      targetSalesId = ownerSalesId;
      targetName = selectedSaleName || "o responsável";
    } else if (creatorGoogleUser) {
      targetSalesId = creatorSalesId;
      targetName = "sua conta Google (criador)";
    }

    // Se os dois estiverem desconectados: NÃO cria de nenhum!
    if (!targetSalesId) {
      const ownerName = selectedSaleName || "O responsável";
      const shouldConnect = window.confirm(
        `Nem ${ownerName} nem você possuem o Google Agenda conectado.\n\nDeseja conectar a sua conta do Google agora para gerar a sala do Meet?`,
      );
      if (!shouldConnect) {
        return;
      }

      try {
        setGeneratingMeet(true);
        notify("Conectando sua conta Google...", { type: "info" });
        await new Promise<void>((resolve, reject) => {
          connectGoogleOAuth(
            () => resolve(),
            (err) => reject(err),
            creatorSalesId,
          );
        });
        targetSalesId = creatorSalesId;
        targetName = "sua conta Google (criador)";
      } catch {
        notify("Não foi possível conectar com o Google.", { type: "error" });
        setGeneratingMeet(false);
        return;
      }
    }

    try {
      setGeneratingMeet(true);
      const targetGcalId =
        taskToEdit?.google_calendar_event_id ||
        generatedGcalEventId ||
        (taskToEdit?.id ? getGCalEventId(taskToEdit.id) : null);

      const rawTitle =
        text.trim() || `Reunião - ${selectedSaleName || "CRM DOTUS"}`;
      const cleanTitle = rawTitle.replace(/^(Reunião:\s*)+/i, "Reunião: ");

      let res = await createGoogleMeetRoom({
        title: cleanTitle,
        startTime: dataInicio ? new Date(dataInicio).toISOString() : undefined,
        endTime: dataFim ? new Date(dataFim).toISOString() : undefined,
        taskId: taskToEdit?.id,
        eventId: targetGcalId || undefined,
        salesId: targetSalesId,
        attendees: convidados.length > 0 ? convidados : undefined,
      });

      // Se a sessão expirou no Google (401 / needsAuth), tenta renovar o token do usuário alvo
      if (!res.success && res.needsAuth) {
        notify(`A sessão Google de ${targetName} expirou. Reconectando...`, {
          type: "info",
        });
        try {
          await new Promise<void>((resolve, reject) => {
            connectGoogleOAuth(
              () => resolve(),
              (err) => reject(err),
              targetSalesId,
            );
          });

          res = await createGoogleMeetRoom({
            title: cleanTitle,
            startTime: dataInicio
              ? new Date(dataInicio).toISOString()
              : undefined,
            endTime: dataFim ? new Date(dataFim).toISOString() : undefined,
            taskId: taskToEdit?.id,
            eventId: targetGcalId || undefined,
            salesId: targetSalesId,
            attendees: convidados.length > 0 ? convidados : undefined,
          });
        } catch {
          notify(`Falha ao renovar conexão Google de ${targetName}.`, {
            type: "error",
          });
          setGeneratingMeet(false);
          return;
        }
      }

      if (res.success && res.meetLink) {
        setLinkChamada(res.meetLink);
        if (res.eventId) {
          setGeneratedGcalEventId(res.eventId);
          setGeneratedGcalSalesId(String(targetSalesId));
          if (taskToEdit?.id) {
            setGCalEventId(taskToEdit.id, res.eventId);
          }
        }
        try {
          await navigator.clipboard.writeText(res.meetLink);
        } catch {}
        notify(
          taskToEdit
            ? `Link do Google Meet gerado na agenda de ${targetName} e vinculado!`
            : `Link do Google Meet gerado na agenda de ${targetName} e copiado!`,
          { type: "success" },
        );
      } else {
        notify(res.message, { type: "error" });
      }
    } catch {
      notify("Erro ao gerar sala no Google Meet", { type: "error" });
    } finally {
      setGeneratingMeet(false);
    }
  };

  const handleSalesChange = (newSalesId: string) => {
    if (
      generatedGcalEventId &&
      generatedGcalSalesId &&
      String(generatedGcalSalesId) !== String(newSalesId)
    ) {
      deleteEventFromGoogleCalendar({
        eventId: generatedGcalEventId,
        salesId: generatedGcalSalesId,
      }).catch(() => {});
      setGeneratedGcalEventId(null);
      setGeneratedGcalSalesId(null);
      setLinkChamada("");
      notify(
        "O responsável foi alterado. Gere o Google Meet novamente caso queira vincular à agenda do novo responsável.",
        { type: "info" },
      );
    }
    setSalesId(newSalesId);
  };

  const handleDialogClose = (newOpen: boolean) => {
    if (!newOpen) {
      if (generatedGcalEventId && !taskToEdit) {
        deleteEventFromGoogleCalendar({
          eventId: generatedGcalEventId,
          salesId: generatedGcalSalesId,
        }).catch(() => {});
        setGeneratedGcalEventId(null);
        setGeneratedGcalSalesId(null);
      }
    }
    onOpenChange(newOpen);
  };

  const selectedDeal = deals.find((d) => String(d.id) === String(dealId));
  const selectedContact = contacts.find(
    (c) => String(c.id) === String(contactId),
  );
  const selectedCompany = companies.find(
    (c) => String(c.id) === String(companyId),
  );
  const selectedSale = sales.find((s) => String(s.id) === String(salesId));
  const selectedSaleName = selectedSale
    ? `${selectedSale.first_name}${selectedSale.last_name ? " " + selectedSale.last_name : ""}`
    : null;

  // Lista de e-mails extraídos do contato selecionado (para adicionar com 1 clique)
  const contactEmails = useMemo(() => {
    if (!selectedContact) return [];
    const list: string[] = [];
    if (Array.isArray(selectedContact.email_jsonb)) {
      selectedContact.email_jsonb.forEach((item) => {
        if (item?.email?.trim()) {
          const em = item.email.trim();
          if (!list.includes(em)) list.push(em);
        }
      });
    }
    const directEmail = (selectedContact as any).email;
    if (
      directEmail &&
      typeof directEmail === "string" &&
      directEmail.trim() &&
      !list.includes(directEmail.trim())
    ) {
      list.push(directEmail.trim());
    }
    return list;
  }, [selectedContact]);

  const handleAddConvidado = (emailToAdd?: string) => {
    const email = (emailToAdd || novoConvidado).trim().toLowerCase();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      notify("Por favor, insira um e-mail válido para o convidado", {
        type: "warning",
      });
      return;
    }

    if (convidados.map((c) => c.toLowerCase()).includes(email)) {
      notify("Este e-mail já foi adicionado aos convidados", { type: "info" });
      if (!emailToAdd) setNovoConvidado("");
      return;
    }

    setConvidados((prev) => [...prev, email]);
    if (!emailToAdd) setNovoConvidado("");
  };

  const handleRemoveConvidado = (indexToRemove: number) => {
    setConvidados((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const gcalEventId = taskToEdit
    ? taskToEdit.google_calendar_event_id || getGCalEventId(taskToEdit.id)
    : null;
  const gcalHtmlLink = taskToEdit
    ? taskToEdit.google_calendar_html_link || getGCalHtmlLink(taskToEdit.id)
    : null;

  const handleCallInitiated = (info: {
    phone: string;
    contactName: string;
    timestamp: Date;
  }) => {
    const formattedDate = format(info.timestamp, "dd/MM/yyyy 'às' HH:mm");
    const logEntry = `📞 [Ligação VoIP] Chamada para ${info.contactName} (${info.phone}) iniciada em ${formattedDate}.`;
    setNotes((prev) => (prev ? `${prev}\n\n${logEntry}` : logEntry));
    notify(
      `Chamada iniciada para ${info.contactName}! Registrado nas notas da atividade.`,
      { type: "success" },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-4xl lg:max-w-5xl p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <DialogHeader className="px-5 pt-4 pb-2 border-b border-border/60">
          <div className="flex items-center justify-between pr-6">
            <DialogTitle className="text-base font-semibold">
              {taskToEdit ? "Editar Atividade" : "Nova Atividade na Agenda"}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {((selectedDeal && dealId !== "none") ||
                (contactId && contactId !== "none")) && (
                <ActivityCallButton
                  dealId={dealId !== "none" ? dealId : null}
                  dealName={selectedDeal?.name}
                  taskId={taskToEdit?.id}
                  contactId={contactId !== "none" ? contactId : null}
                  companyId={companyId !== "none" ? companyId : null}
                  onCallInitiated={handleCallInitiated}
                  variant="header"
                />
              )}
              {gcalEventId && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Sincronizado com Google Agenda
                  </span>
                  {gcalHtmlLink && (
                    <a
                      href={gcalHtmlLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      Abrir no Google
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Layout Dividido: Formulário na Esquerda e Mini Agenda do Dia na Direita */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Lado Esquerdo: Formulário */}
          <form
            onSubmit={handleSubmit}
            className="flex-1 p-5 overflow-y-auto space-y-4 max-h-[80vh]"
          >
            {/* Título da Atividade */}
            <div className="space-y-1">
              <Input
                id="act-text"
                placeholder="Ex: Chamada - Rodas eletrons"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  userModifiedTitleRef.current = true;
                }}
                className="h-10 text-sm font-semibold border-2 border-border/80 focus-visible:border-primary rounded-lg"
                required
                autoFocus
              />
            </div>

            {/* Barra de Seleção Rápida de Tipos (com ícones) */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {Object.values(ACTIVITY_TYPES).map((item) => {
                const Icon = item.icon;
                const isSelected = type === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => handleTypeSelect(item.value)}
                    className={cn(
                      "px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 text-xs font-medium transition-all cursor-pointer",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary shadow-xs"
                        : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground border-border/70",
                    )}
                    title={item.label}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Linha de Data e Hora Estilo Google Calendar */}
            <div className="flex items-center gap-2 p-2.5 rounded-lg border border-border/70 bg-card/60 flex-wrap">
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />

              <Input
                type="date"
                value={startDateStr}
                onChange={(e) => {
                  const newD = e.target.value;
                  setDataInicio(`${newD}T${startTimeStr}`);
                  if (dataFim && endTimeStr) {
                    setDataFim(`${newD}T${endTimeStr}`);
                  }
                }}
                className="h-7 text-xs w-[130px]"
                title="Data de início"
              />

              <Input
                type="time"
                value={startTimeStr}
                onChange={(e) => {
                  const newT = e.target.value;
                  setDataInicio(`${startDateStr}T${newT}`);
                }}
                className="h-7 text-xs w-[85px]"
                title="Horário de início"
              />

              <span className="text-muted-foreground text-xs font-bold">
                até
              </span>

              <Input
                type="time"
                value={endTimeStr}
                onChange={(e) => {
                  const newT = e.target.value;
                  if (!newT) {
                    setDataFim("");
                  } else {
                    const d = endDateStr || startDateStr;
                    setDataFim(`${d}T${newT}`);
                  }
                }}
                placeholder="--:--"
                className="h-7 text-xs w-[85px]"
                title="Horário de término (opcional)"
              />

              <Input
                type="date"
                value={endDateStr}
                onChange={(e) => {
                  const newD = e.target.value;
                  if (!newD) {
                    setDataFim("");
                  } else {
                    setDataFim(
                      `${newD}T${endTimeStr || startTimeStr || "11:00"}`,
                    );
                  }
                }}
                className="h-7 text-xs w-[130px]"
                title="Data de término (opcional)"
              />

              {dataFim && (
                <button
                  type="button"
                  onClick={() => setDataFim("")}
                  className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                  title="Limpar horário de término (registrar apenas início)"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Presets Rápidos de Duração */}
            <div className="flex items-center gap-1.5 flex-wrap -mt-1 text-xs">
              <span className="text-[10px] uppercase font-bold text-muted-foreground mr-1">
                Duração:
              </span>
              <button
                type="button"
                onClick={() => setDataFim("")}
                className={cn(
                  "px-2 py-0.5 rounded-md border text-[11px] font-medium transition-colors cursor-pointer",
                  !dataFim
                    ? "bg-primary text-primary-foreground border-primary font-semibold shadow-2xs"
                    : "border-border/70 bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground",
                )}
                title="Registrar apenas no horário de início"
              >
                Apenas Início
              </button>
              {[
                { label: "15m", minutes: 15 },
                { label: "30m", minutes: 30 },
                { label: "45m", minutes: 45 },
                { label: "1h", minutes: 60 },
                { label: "1h30", minutes: 90 },
                { label: "2h", minutes: 120 },
              ].map((preset) => (
                <button
                  key={preset.minutes}
                  type="button"
                  onClick={() => {
                    const baseStart = dataInicio
                      ? new Date(dataInicio)
                      : new Date();
                    const newEnd = new Date(
                      baseStart.getTime() + preset.minutes * 60 * 1000,
                    );
                    setDataFim(format(newEnd, "yyyy-MM-dd'T'HH:mm"));
                  }}
                  className="px-2 py-0.5 rounded-md border border-border/70 bg-muted/40 hover:bg-muted text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  {preset.label}
                </button>
              ))}

              <div className="flex items-center gap-1 ml-auto">
                <button
                  type="button"
                  onClick={() => {
                    const baseStart = dataInicio
                      ? new Date(dataInicio)
                      : new Date();
                    const currentEnd = dataFim
                      ? new Date(dataFim)
                      : new Date(baseStart.getTime() + 60 * 60 * 1000);
                    const newEnd = new Date(
                      Math.max(
                        baseStart.getTime() + 15 * 60 * 1000,
                        currentEnd.getTime() - 15 * 60 * 1000,
                      ),
                    );
                    setDataFim(format(newEnd, "yyyy-MM-dd'T'HH:mm"));
                  }}
                  className="px-1.5 py-0.5 rounded-md border border-border/70 bg-muted/40 hover:bg-muted text-[11px] font-mono font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  title="Diminuir 15 minutos"
                >
                  -15m
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const baseStart = dataInicio
                      ? new Date(dataInicio)
                      : new Date();
                    const currentEnd = dataFim
                      ? new Date(dataFim)
                      : new Date(baseStart.getTime() + 60 * 60 * 1000);
                    const newEnd = new Date(
                      currentEnd.getTime() + 15 * 60 * 1000,
                    );
                    setDataFim(format(newEnd, "yyyy-MM-dd'T'HH:mm"));
                  }}
                  className="px-1.5 py-0.5 rounded-md border border-border/70 bg-muted/40 hover:bg-muted text-[11px] font-mono font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  title="Aumentar 15 minutos"
                >
                  +15m
                </button>
              </div>
            </div>

            {/* Prioridade e Link de Vídeo Chamada */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Prioridade</Label>
                <Select
                  value={prioridade}
                  onValueChange={(val) =>
                    setPrioridade(val as "baixa" | "media" | "alta")
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa" className="text-xs">
                      🟢 Baixa
                    </SelectItem>
                    <SelectItem value="media" className="text-xs">
                      🟡 Média
                    </SelectItem>
                    <SelectItem value="alta" className="text-xs">
                      🔴 Alta
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">
                    Chamada de Vídeo (Meet/Zoom)
                  </Label>
                  <button
                    type="button"
                    disabled={generatingMeet}
                    onClick={handleGenerateMeet}
                    className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 cursor-pointer disabled:opacity-50 transition-colors"
                    title="Criar sala instantânea no Google Meet"
                  >
                    <Sparkles className="w-3 h-3 text-amber-500 animate-pulse" />
                    <span>
                      {generatingMeet ? "Gerando Meet..." : "Gerar Google Meet"}
                    </span>
                  </button>
                </div>
                <div className="relative flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <Input
                      placeholder="https://meet.google.com/..."
                      value={linkChamada}
                      onChange={(e) => setLinkChamada(e.target.value)}
                      className="h-8 text-xs pl-7 pr-8"
                    />
                    <Video className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" />
                    {linkChamada && (
                      <a
                        href={linkChamada}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute right-2 top-2 text-muted-foreground hover:text-primary transition-colors"
                        title="Entrar na reunião agora"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  {linkChamada && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard?.writeText(linkChamada);
                        notify("Link da reunião copiado!", { type: "info" });
                      }}
                      className="h-8 px-2 text-xs shrink-0"
                      title="Copiar link da reunião"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Convidados da Reunião (Google Agenda) */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                  <Users className="w-3.5 h-3.5 text-primary" />
                  <span>Convidados da Reunião (Google Agenda)</span>
                </Label>
                {convidados.length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                    {convidados.length}{" "}
                    {convidados.length === 1 ? "convidado" : "convidados"}
                  </span>
                )}
              </div>

              {/* Botão rápido para adicionar e-mail do cliente selecionado se houver */}
              {contactEmails.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] text-muted-foreground">
                    E-mail do cliente:
                  </span>
                  {contactEmails.map((cEmail) => {
                    const alreadyAdded = convidados
                      .map((c) => c.toLowerCase())
                      .includes(cEmail.toLowerCase());
                    return (
                      <button
                        key={cEmail}
                        type="button"
                        onClick={() =>
                          !alreadyAdded && handleAddConvidado(cEmail)
                        }
                        disabled={alreadyAdded}
                        className={cn(
                          "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border transition-all cursor-pointer",
                          alreadyAdded
                            ? "bg-muted/50 text-muted-foreground/60 border-border/40 cursor-default"
                            : "bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 hover:border-primary/40 font-medium",
                        )}
                        title={
                          alreadyAdded
                            ? "Já adicionado à lista"
                            : `Adicionar ${cEmail} como convidado`
                        }
                      >
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[220px]">{cEmail}</span>
                        {!alreadyAdded && (
                          <Plus className="w-2.5 h-2.5 ml-0.5" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Input manual para adicionar qualquer e-mail */}
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <Input
                    placeholder="Adicionar e-mail do convidado (ex: cliente@empresa.com)..."
                    value={novoConvidado}
                    onChange={(e) => setNovoConvidado(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddConvidado();
                      }
                    }}
                    className="h-8 text-xs pl-7"
                  />
                  <Mail className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAddConvidado()}
                  className="h-8 px-2.5 text-xs shrink-0 font-medium"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar
                </Button>
              </div>

              {/* Lista de convidados adicionados com chips removíveis */}
              {convidados.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                  {convidados.map((email, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-muted/80 border border-border text-foreground hover:bg-muted transition-colors"
                    >
                      <Mail className="w-3 h-3 text-muted-foreground" />
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveConvidado(idx)}
                        className="p-0.5 hover:text-destructive rounded transition-colors text-muted-foreground hover:bg-background/80"
                        title="Remover convidado"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground">
                  Ao salvar ou gerar a reunião, o Google Agenda enviará convites
                  automáticos por e-mail com a sala do Google Meet.
                </p>
              )}
            </div>

            {/* Proprietário da Atividade / Agenda (Responsável) */}
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                  <User className="w-3.5 h-3.5 text-primary" />
                  <span>Proprietário da Atividade (Responsável)</span>
                </Label>
                {selectedSale && (
                  <span className="text-[10px] text-muted-foreground">
                    Agenda:{" "}
                    <strong className="text-foreground">
                      {selectedSaleName}
                    </strong>
                  </span>
                )}
              </div>
              <Select value={salesId} onValueChange={handleSalesChange}>
                <SelectTrigger className="h-8.5 text-xs">
                  <SelectValue placeholder="Selecione o proprietário da atividade..." />
                </SelectTrigger>
                <SelectContent>
                  {sales.map((sale) => {
                    const isCurrent =
                      identity?.id && String(identity.id) === String(sale.id);
                    return (
                      <SelectItem
                        key={sale.id}
                        value={String(sale.id)}
                        className="text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                            {sale.first_name?.[0] || "U"}
                          </span>
                          <span className="font-medium">
                            {sale.first_name} {sale.last_name || ""}
                          </span>
                          {sale.email && (
                            <span className="text-[11px] text-muted-foreground ml-1">
                              ({sale.email})
                            </span>
                          )}
                          {isCurrent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold ml-auto">
                              Você
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Ao selecionar o proprietário, o calendário ao lado atualiza em
                tempo real para exibir a agenda dele e evitar conflitos.
              </p>
            </div>

            {/* Vinculações (Negócio, Pessoa e Organização) com estilo Chip e botão X */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Associações do CRM
              </p>

              {/* Negócio */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
                  <Handshake className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                {dealId !== "none" ? (
                  <div className="flex-1 flex items-center justify-between px-2.5 py-1 rounded-lg border border-border bg-card text-xs font-medium">
                    <span className="truncate">
                      {selectedDeal?.name || "Carregando negócio..."}
                    </span>
                    <div className="flex items-center gap-1.5 ml-2">
                      <ActivityCallButton
                        dealId={dealId}
                        dealName={selectedDeal?.name}
                        taskId={taskToEdit?.id}
                        contactId={contactId !== "none" ? contactId : null}
                        companyId={companyId !== "none" ? companyId : null}
                        onCallInitiated={handleCallInitiated}
                        variant="inline"
                      />
                      <button
                        type="button"
                        onClick={() => setDealId("none")}
                        className="p-0.5 hover:text-destructive rounded transition-colors"
                        title="Desvincular negócio"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <Select value={dealId} onValueChange={handleDealChange}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Conectar a um negócio..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">
                        Nenhum negócio
                      </SelectItem>
                      {deals.map((deal) => (
                        <SelectItem
                          key={deal.id}
                          value={String(deal.id)}
                          className="text-xs"
                        >
                          {deal.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Pessoa / Contato */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                {contactId !== "none" ? (
                  <div className="flex-1 flex items-center justify-between px-2.5 py-1 rounded-lg border border-border bg-card text-xs font-medium">
                    <span className="truncate">
                      {selectedContact
                        ? `${selectedContact.first_name} ${selectedContact.last_name || ""}`.trim()
                        : "Carregando contato..."}
                    </span>
                    <div className="flex items-center gap-1.5 ml-2">
                      <ActivityCallButton
                        dealId={dealId !== "none" ? dealId : null}
                        dealName={selectedDeal?.name}
                        taskId={taskToEdit?.id}
                        contactId={contactId !== "none" ? contactId : null}
                        companyId={companyId !== "none" ? companyId : null}
                        onCallInitiated={handleCallInitiated}
                        variant="inline"
                      />
                      <button
                        type="button"
                        onClick={() => setContactId("none")}
                        className="p-0.5 hover:text-destructive rounded transition-colors"
                        title="Desvincular contato"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <Select value={contactId} onValueChange={setContactId}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Conectar a uma pessoa..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">
                        Nenhuma pessoa
                      </SelectItem>
                      {contacts.map((contact) => (
                        <SelectItem
                          key={contact.id}
                          value={String(contact.id)}
                          className="text-xs"
                        >
                          {contact.first_name} {contact.last_name || ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Organização / Empresa */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                {companyId !== "none" ? (
                  <div className="flex-1 flex items-center justify-between px-2.5 py-1 rounded-lg border border-border bg-card text-xs font-medium">
                    <span className="truncate">
                      {selectedCompany?.name || "Carregando organização..."}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCompanyId("none")}
                      className="p-0.5 hover:text-destructive rounded transition-colors ml-2"
                      title="Desvincular organização"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <Select value={companyId} onValueChange={setCompanyId}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Conectar a uma organização..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">
                        Nenhuma organização
                      </SelectItem>
                      {companies.map((company) => (
                        <SelectItem
                          key={company.id}
                          value={String(company.id)}
                          className="text-xs"
                        >
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Notas e Observações da Atividade */}
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                  <FileText className="w-3.5 h-3.5 text-primary" />
                  <span>Notas / Observações da Atividade</span>
                </Label>
                <div className="flex items-center gap-2">
                  {notes.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPreviewNotes(!previewNotes)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline cursor-pointer"
                    >
                      {previewNotes ? (
                        <>
                          <Edit2 className="w-3 h-3" />
                          <span>Escrever</span>
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" />
                          <span>Visualizar formatado</span>
                        </>
                      )}
                    </button>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {notes.length > 0
                      ? `${notes.length} caracteres`
                      : "Opcional"}
                  </span>
                </div>
              </div>

              {!previewNotes && (
                <NoteFormattingToolbar
                  textareaRef={notesTextareaRef}
                  onTextChange={setNotes}
                  className="mb-1.5"
                />
              )}

              {previewNotes ? (
                <div className="min-h-[76px] pb-1">
                  <ActivityNoteBox notes={notes} className="mt-0" />
                </div>
              ) : (
                <Textarea
                  ref={notesTextareaRef}
                  placeholder="Escreva anotações, pauta da reunião, resumo da conversa, pontos discutidos ou próximos passos (suporta Markdown e HTML)..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="text-xs resize-y min-h-[72px] bg-background/50 border-border/80 focus-visible:border-primary placeholder:text-muted-foreground/60 leading-relaxed font-normal"
                />
              )}

              {/* Botões de modelo rápido */}
              <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                <span className="text-[10px] text-muted-foreground font-medium">
                  Modelos rápidos:
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const template =
                      "📋 Pauta da Reunião:\n1. Alinhamento de proposta\n2. Dúvidas técnicas\n3. Definição de prazos";
                    setNotes((prev) =>
                      prev ? `${prev}\n\n${template}` : template,
                    );
                  }}
                  className="text-[10px] px-2 py-0.5 rounded bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60 transition-colors cursor-pointer"
                >
                  + Pauta
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const template =
                      "📞 Resumo da Conversa:\n• Cliente interessado em:\n• Objeções / Dúvidas:\n• Valor acordado:";
                    setNotes((prev) =>
                      prev ? `${prev}\n\n${template}` : template,
                    );
                  }}
                  className="text-[10px] px-2 py-0.5 rounded bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60 transition-colors cursor-pointer"
                >
                  + Resumo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const template =
                      "🚀 Próximos Passos:\n• Ação a realizar:\n• Responsável:\n• Data limite:";
                    setNotes((prev) =>
                      prev ? `${prev}\n\n${template}` : template,
                    );
                  }}
                  className="text-[10px] px-2 py-0.5 rounded bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60 transition-colors cursor-pointer"
                >
                  + Próximos Passos
                </button>
                {notes && (
                  <button
                    type="button"
                    onClick={() => setNotes("")}
                    className="text-[10px] text-muted-foreground hover:text-destructive transition-colors ml-auto cursor-pointer"
                  >
                    Limpar notas
                  </button>
                )}
              </div>
            </div>

            {/* Rodapé do Formulário */}
            <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
                  <Checkbox
                    checked={isDone}
                    onCheckedChange={(checked) => setIsDone(Boolean(checked))}
                  />
                  <span>Marcar como feito</span>
                </label>

                {getConnectedGoogleUser(
                  salesId !== "none" ? salesId : identity?.id,
                ) ? (
                  !isTaskTypeAllowedForGCalSync(
                    type,
                    salesId !== "none" ? salesId : identity?.id,
                  ) ? (
                    <span
                      className="text-[11px] text-muted-foreground flex items-center gap-1.5"
                      title="Para alterar, acesse Meu Perfil > Sincronização com Google Agenda"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500/80 shrink-0" />
                      <span>
                        Atividade desmarcada no perfil para o Google Agenda
                      </span>
                    </span>
                  ) : (
                    <label className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 font-medium cursor-pointer select-none">
                      <Checkbox
                        checked={syncGoogleCalendar}
                        onCheckedChange={(checked) =>
                          setSyncGoogleCalendar(Boolean(checked))
                        }
                      />
                      <span>Sincronizar com Google Agenda</span>
                    </label>
                  )
                ) : (
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500/70 shrink-0" />
                    <span>
                      Google Agenda não conectada para este responsável
                    </span>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {taskToEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteTask}
                    disabled={saving || deleting}
                    className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 border-red-200 dark:border-red-900"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    <span>{deleting ? "Excluindo..." : "Excluir"}</span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDialogClose(false)}
                  disabled={saving || deleting}
                  className="h-8 text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="sm"
                  disabled={saving || deleting}
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  {saving ? "Salvando..." : taskToEdit ? "Atualizar" : "Salvar"}
                </Button>
              </div>
            </div>
          </form>

          {/* Lado Direito: Mini Painel da Agenda do Dia Selecionado */}
          <div className="w-full md:w-[360px] lg:w-[400px] border-t md:border-t-0 md:border-l border-border/60 bg-muted/15 p-4 flex flex-col shrink-0">
            <ActivityMiniDayTimeline
              selectedDate={selectedCalendarDate}
              onDateChange={handleTimelineDateChange}
              startTime={startTimeStr}
              endTime={endTimeStr}
              currentType={type}
              currentTitle={text}
              onSelectSlot={handleTimelineSlotSelect}
              excludeTaskId={taskToEdit?.id}
              salesId={salesId !== "none" ? salesId : null}
              ownerName={selectedSaleName}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
